import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Plus, Search, Edit, Trash2, Eye, Brain, UserPlus, Home, Building2, Layers, AlertCircle, CheckCircle2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import CaptchaWidget from "@/components/CaptchaWidget";
import { validateEmail, validateIndianPhone } from "@/utils/validation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Leads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [deleteId, setDeleteId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  
  // Lead assignment state
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assigningLead, setAssigningLead] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState("");

  const [overdueleads, setOverdueLeads] = useState([]);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setCurrentUser(user);
    fetchLeads();
    fetchDashboardStats();
    if (user.role === "admin" || user.role === "team_leader") {
      fetchAssignableUsers();
      fetchOverdueLeads();
    }
  }, []);

  // Refetch when sourcing manager changes context
  useEffect(() => {
    const handler = () => { fetchLeads(); fetchDashboardStats(); };
    window.addEventListener('context-changed', handler);
    return () => window.removeEventListener('context-changed', handler);
  }, []);

  useEffect(() => {
    filterLeads();
  }, [leads, searchTerm, filterStatus]);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/reports/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.log("Could not fetch dashboard stats");
    }
  };

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(response.data);
    } catch (error) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignableUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users/assignable`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssignableUsers(response.data);
    } catch (error) {
      console.log("Could not fetch assignable users");
    }
  };

  const fetchOverdueLeads = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/leads/escalations/overdue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOverdueLeads(response.data);
    } catch (error) {
      console.log("Could not fetch overdue leads");
    }
  };

  const filterLeads = () => {
    let filtered = leads;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (l) =>
          l.name.toLowerCase().includes(term) ||
          (currentUser?.role !== 'sourcing_manager' && l.email.toLowerCase().includes(term)) ||
          (currentUser?.role !== 'sourcing_manager' && l.phone.includes(searchTerm))
      );
    }

    if (filterStatus !== "all") {
      filtered = filtered.filter((l) => l.status === filterStatus);
    }

    // Sort by AI score (highest first)
    filtered.sort((a, b) => (b.ai_score || 0) - (a.ai_score || 0));

    setFilteredLeads(filtered);
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/leads/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Lead deleted successfully");
      setLeads(leads.filter((l) => l.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const calculateLeadScore = async (leadId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/leads/${leadId}/score`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`AI Score calculated: ${response.data.ai_score.toFixed(0)}`);
      // Refresh leads
      fetchLeads();
    } catch (error) {
      toast.error("Failed to calculate AI score");
    }
  };

  const openAssignDialog = (lead) => {
    setAssigningLead(lead);
    setSelectedAssignee(lead.assigned_to || "");
    setShowAssignDialog(true);
  };

  const handleAssignLead = async () => {
    if (!selectedAssignee) {
      toast.error("Please select an assignee");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/leads/${assigningLead.id}/assign`,
        { assigned_to: selectedAssignee },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowAssignDialog(false);
      setAssigningLead(null);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign lead");
    }
  };

  const getAssigneeName = (assignedTo) => {
    const user = assignableUsers.find(u => u.id === assignedTo);
    return user ? user.full_name : "Unassigned";
  };

  const canAssignLeads = currentUser?.role === "admin" || currentUser?.role === "team_leader";

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0f2744] border-t-transparent" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0f2744] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Leads
            </h1>
            <p className="text-slate-500 mt-1">
              Manage and track your leads
            </p>
          </div>
          <Button
            data-testid="add-lead-button"
            onClick={() => navigate("/leads/new")}
            className="bg-gradient-to-r from-[#c9a227] to-[#ae8220] hover:from-[#ae8220] hover:to-[#96700f] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            style={{ boxShadow: '0 10px 25px -5px rgba(201, 162, 39, 0.4)' }}
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Lead
          </Button>
        </div>

        {/* Lead Summary Cards */}
        {dashboardStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Total Leads */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Total</p>
                  <p className="text-3xl font-bold text-[#0f2744] mt-1">{dashboardStats.total_leads || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-[#0f2744]/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-[#0f2744]" />
                </div>
              </div>
            </div>

            {/* Residential Leads */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Residential</p>
                  <p className="text-3xl font-bold text-blue-600 mt-1">{dashboardStats.leads_by_property_type?.residential || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Home className="h-5 w-5 text-blue-500" />
                </div>
              </div>
            </div>

            {/* Commercial Leads */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Commercial</p>
                  <p className="text-3xl font-bold text-[#c9a227] mt-1">{dashboardStats.leads_by_property_type?.commercial || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-[#c9a227]/10 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-[#c9a227]" />
                </div>
              </div>
            </div>

            {/* Other Leads */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Others</p>
                  <p className="text-3xl font-bold text-slate-600 mt-1">{dashboardStats.leads_by_property_type?.others || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-slate-500/10 flex items-center justify-center">
                  <Layers className="h-5 w-5 text-slate-500" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SLA Escalation Alerts - Admin Only */}
        {(currentUser?.role === 'admin' || currentUser?.role === 'team_leader') && overdueleads.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="sla-alerts-panel">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <h3 className="text-sm font-bold text-red-800 uppercase tracking-wide">SLA Breach Alerts ({overdueleads.length})</h3>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
              {overdueleads.map((item, idx) => (
                <div key={idx} className="bg-white rounded-lg border border-red-100 p-3 flex items-center justify-between cursor-pointer hover:border-red-300 transition-colors"
                  onClick={() => navigate(`/leads/${item.lead_id}`)}
                  data-testid={`sla-alert-${idx}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{item.lead_name}</p>
                    <p className="text-xs text-slate-500 truncate">{item.property_title} - {item.status_label}</p>
                  </div>
                  <div className="text-right ml-3 flex-shrink-0">
                    <p className="text-sm font-bold text-red-600">{item.hours_elapsed >= 48 ? `${Math.floor(item.hours_elapsed/24)}d ${Math.floor(item.hours_elapsed%24)}h` : `${Math.floor(item.hours_elapsed)}h`}</p>
                    <p className="text-xs text-slate-400">SLA: {item.max_hours}h</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                data-testid="search-leads-input"
                type="text"
                placeholder="Search leads by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 rounded-lg border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0f2744] focus:ring-[#0f2744]"
              />
            </div>
            <select
              data-testid="filter-status-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-11 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm cursor-pointer focus:bg-white focus:border-[#0f2744] focus:ring-1 focus:ring-[#0f2744]"
            >
              <option value="all">All Status</option>
              <option value="new_web_lead">New Web Lead</option>
              <option value="assigned">Assigned</option>
              <option value="attempted_contact">Attempted Contact</option>
              <option value="contacted">Contacted</option>
              <option value="requirement_gathered">Requirement Gathered</option>
              <option value="pitched">Pitched</option>
              <option value="site_visit_scheduled">Site Visit Scheduled</option>
              <option value="site_visit_completed">Site Visit Completed</option>
              <option value="in_negotiation">In Negotiation</option>
              <option value="closed_won">Closed - Won</option>
              <option value="closed_lost">Closed - Lost</option>
            </select>
          </div>
        </div>

        {/* Leads Table */}
        {filteredLeads.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-16 text-center">
            <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">No leads found</p>
            <Button
              onClick={() => navigate("/leads/new")}
              className="mt-4 bg-[#0f2744] hover:bg-[#1e3a5f] text-white rounded-lg"
            >
              Add Your First Lead
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" style={{ boxShadow: '0 4px 20px -5px rgba(15, 39, 68, 0.1)' }}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-[#0f2744] to-[#1e3a5f]">
                  <tr>
                    <th className="px-4 md:px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Lead</th>
                    <th className="px-4 md:px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Contact</th>
                    <th className="hidden md:table-cell px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Property</th>
                    <th className="hidden lg:table-cell px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Source</th>
                    <th className="px-4 md:px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Status</th>
                    {canAssignLeads && (
                      <th className="hidden lg:table-cell px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Assigned To</th>
                    )}
                    <th className="hidden md:table-cell px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">AI Score</th>
                    <th className="px-4 md:px-6 py-4 text-right text-xs font-bold text-white uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLeads.map((lead, index) => (
                    <tr key={lead.id} data-testid={`lead-row-${lead.id}`} className={`hover:bg-slate-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <td className="px-4 md:px-6 py-4">
                        <p className="font-semibold text-[#0f2744]">{lead.name}</p>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        {currentUser?.role === 'sourcing_manager' ? (
                          <p className="text-xs text-slate-400 italic">Contact admin for details</p>
                        ) : (
                          <>
                            <p className="text-sm text-slate-900">{lead.email}</p>
                            <p className="text-sm text-slate-500">{lead.phone}</p>
                          </>
                        )}
                      </td>
                      <td className="hidden md:table-cell px-6 py-4">
                        {lead.property_title ? (
                          <p className="text-sm text-[#c9a227] font-semibold">{lead.property_title}</p>
                        ) : (
                          <p className="text-sm text-slate-400">-</p>
                        )}
                      </td>
                      <td className="hidden lg:table-cell px-6 py-4">
                        <span className="text-xs px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full uppercase font-bold">
                          {lead.source}
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <span className={`text-xs px-3 py-1.5 rounded-full uppercase font-bold whitespace-nowrap ${
                          lead.status === 'new_web_lead' || lead.status === 'new' ? 'bg-blue-100 text-blue-700' :
                          lead.status === 'assigned' ? 'bg-indigo-100 text-indigo-700' :
                          lead.status === 'attempted_contact' ? 'bg-orange-100 text-orange-700' :
                          lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-700' :
                          lead.status === 'requirement_gathered' || lead.status === 'qualified' ? 'bg-purple-100 text-purple-700' :
                          lead.status === 'pitched' ? 'bg-cyan-100 text-cyan-700' :
                          lead.status === 'site_visit_scheduled' ? 'bg-teal-100 text-teal-700' :
                          lead.status === 'site_visit_completed' ? 'bg-emerald-100 text-emerald-700' :
                          lead.status === 'in_negotiation' ? 'bg-amber-100 text-amber-700' :
                          lead.status === 'closed_won' || lead.status === 'converted' ? 'bg-green-100 text-green-700' :
                          lead.status === 'closed_lost' || lead.status === 'lost' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>{(lead.status || '').replace(/_/g, ' ')}</span>
                      </td>
                      {canAssignLeads && (
                        <td className="hidden lg:table-cell px-6 py-4">
                          <button
                            data-testid={`assign-lead-${lead.id}`}
                            onClick={() => openAssignDialog(lead)}
                            className="text-sm text-[#0f2744] hover:text-[#c9a227] font-medium flex items-center gap-1 hover:underline transition-colors"
                          >
                            <UserPlus className="h-4 w-4" />
                            {getAssigneeName(lead.assigned_to)}
                          </button>
                        </td>
                      )}
                      <td className="hidden md:table-cell px-6 py-4">
                        {lead.ai_score ? (
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-[#1e3a5f]">{lead.ai_score.toFixed(0)}</span>
                            <button
                              data-testid={`recalculate-score-${lead.id}`}
                              onClick={() => calculateLeadScore(lead.id)}
                              className="text-slate-400 hover:text-[#d4af37] transition-colors"
                              title="Recalculate Score"
                            >
                              <Brain className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            data-testid={`calculate-score-${lead.id}`}
                            onClick={() => calculateLeadScore(lead.id)}
                            className="text-sm text-[#1e3a5f] hover:text-[#d4af37] font-medium flex items-center gap-1 transition-colors"
                          >
                            <Brain className="h-4 w-4" />
                            Calculate
                          </button>
                        )}
                      </td>
                      <td className="px-4 md:px-6 py-4 text-right">
                        <div className="flex justify-end gap-1 md:gap-2">
                          <Button
                            data-testid={`view-lead-${lead.id}`}
                            onClick={() => navigate(`/leads/${lead.id}`)}
                            size="sm"
                            variant="ghost"
                            className="rounded-lg hover:bg-[#f0f4f8]"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            data-testid={`edit-lead-${lead.id}`}
                            onClick={() => navigate(`/leads/edit/${lead.id}`)}
                            size="sm"
                            variant="outline"
                            className="rounded-lg"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            data-testid={`delete-lead-${lead.id}`}
                            onClick={() => setDeleteId(lead.id)}
                            size="sm"
                            variant="destructive"
                            className="rounded-lg"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Lead Assignment Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-[#1e3a5f]">Assign Lead</DialogTitle>
              <DialogDescription>
                Assign &ldquo;{assigningLead?.name}&rdquo; to a team member.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="assignee" className="text-[#1e3a5f] font-semibold">Assign To</Label>
                <select
                  id="assignee"
                  data-testid="assignee-select"
                  value={selectedAssignee}
                  onChange={(e) => setSelectedAssignee(e.target.value)}
                  className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f]"
                >
                  <option value="">Select a team member</option>
                  {assignableUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} ({user.role === "team_leader" ? "Team Leader" : "Agent"})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAssignDialog(false)}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-assign-button"
                onClick={handleAssignLead}
                className="bg-gradient-to-r from-[#1e3a5f] to-[#162c46] text-white rounded-lg"
              >
                Assign Lead
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Lead</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this lead? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-[#0f2744] hover:bg-[#1e3a5f]">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
